import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../services/adapter.service';
import { SquareOrdersService } from '../services/square-orders.service';
import * as SquareOrdersActions from './square-orders.actions';

@Injectable()
export class SquareOrdersEffects {
    private _actions$ = inject(Actions);
    private _squareOrdersService = inject(SquareOrdersService);
    private _alertService = inject(AlertService);
    private adapter = inject(AdapterService);

    messages = {
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    getAllSquareOrders$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.loadSquareOrders),
            switchMap((action) =>
                this._squareOrdersService.getAll(action.params).pipe(
                    map((res) => {
                        // console.log('[SquareOrders] getAll response:', res);
                        const data = this.adapter.adaptAll(res.data);
                        const totalCount = res.pagination?.total;
                        return SquareOrdersActions.loadSquareOrdersSuccess({
                            squareOrders: data,
                            totalCount,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.loadSquareOrdersFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    getSquareOrder$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.loadSquareOrder),
            switchMap((action) =>
                this._squareOrdersService.getOne(action.id).pipe(
                    map((res) =>
                        SquareOrdersActions.loadSquareOrderSuccess({
                            squareOrder: res.data[0],
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.loadSquareOrderFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    createSquareOrder$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.createSquareOrder),
            switchMap((action) =>
                this._squareOrdersService.create(action.data).pipe(
                    map((res) =>
                        SquareOrdersActions.createSquareOrderSuccess({
                            squareOrder: res.data,
                            success: 'Square order created successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.createSquareOrderFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    updateSquareOrder$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.updateSquareOrder),
            switchMap((action) =>
                this._squareOrdersService.update(action.data, action.id).pipe(
                    map((res) => {
                        // console.log('[SquareOrders] update response:', res);
                        const data = this.adapter.adaptSingle(res.data[0]);
                        return SquareOrdersActions.updateSquareOrderSuccess({
                            squareOrder: data,
                            success: 'Square order updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.updateSquareOrderFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    deleteSquareOrder$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.deleteSquareOrder),
            switchMap((action) =>
                this._squareOrdersService.delete(action.id).pipe(
                    map(() =>
                        SquareOrdersActions.deleteSquareOrderSuccess({
                            id: action.id,
                            success: 'Square order deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.deleteSquareOrderFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    revertOrders$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareOrdersActions.revertOrders),
            switchMap((action) =>
                this._squareOrdersService.revert(action.orderIds).pipe(
                    map((res) => {
                        // console.log('[RevertOrders] Response:', res.data);
                        return SquareOrdersActions.revertOrdersSuccess({
                            data: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            SquareOrdersActions.revertOrdersFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareOrdersActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareOrdersActions.createSquareOrderSuccess,
                    SquareOrdersActions.updateSquareOrderSuccess,
                    SquareOrdersActions.deleteSquareOrderSuccess
                ),
                tap((action) =>
                    this._alertService.showAlert(action.success, 'success')
                )
            ),
        { dispatch: false }
    );

    onSquareOrdersActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareOrdersActions.loadSquareOrdersFailure,
                    SquareOrdersActions.loadSquareOrderFailure,
                    SquareOrdersActions.createSquareOrderFailure,
                    SquareOrdersActions.updateSquareOrderFailure,
                    SquareOrdersActions.deleteSquareOrderFailure
                ),
                tap((action) =>
                    this._alertService.showAlert(action.error, 'error')
                )
            ),
        { dispatch: false }
    );
}
